import { type SiteDocument, type SitePage, type SiteSection, siteDocumentSchema } from '../domains/site/schema';
import {
  type SiteWorkspaceComponent,
  type SiteWorkspaceTarget,
  type SiteWorkspace,
  type SiteWorkspaceFile,
  type SiteWorkspaceRevision,
  type SiteWorkspaceRoute,
  siteWorkspaceRevisionSchema,
  siteWorkspaceSchema,
} from '../domains/site-workspace/schema';
import { formatBridgeRouteOverrideComment } from './site-workspace-bridge';
import { collectSectionPromptCandidates } from './site-prompt-targeting';
import {
  collectWorkspaceSourceDependencyFiles,
  getSectionTemplateDefinition,
  sharedWorkspaceCodeFirstEntryFilePaths,
} from './site-workspace-section-templates';
import { apiRequest } from './api-client';
import { hasWorkspaceRuntimeDefaultExport } from './site-workspace-runtime';

type SiteWorkspaceRow = {
  site_id: string;
  version: number;
  workspace: unknown;
  updated_at?: string;
};

type SiteWorkspaceRevisionRow = {
  id: string;
  site_id: string;
  version: number;
  summary: string;
  created_at: string;
  workspace?: unknown;
};

export function isWorkspaceStorageUnavailableError(error: unknown) {
  const message =
    typeof error === 'object' && error && 'message' in error
      ? String(error.message ?? '')
      : '';
  const details =
    typeof error === 'object' && error && 'details' in error
      ? String(error.details ?? '')
      : '';
  const hint =
    typeof error === 'object' && error && 'hint' in error
      ? String(error.hint ?? '')
      : '';
  const combined = `${message} ${details} ${hint}`;

  return /site_workspaces|site_workspace_revisions/iu.test(combined)
    && /(does not exist|Could not find the table|relation .* does not exist)/iu.test(combined);
}

function cloneWorkspace(workspace: SiteWorkspace): SiteWorkspace {
  return siteWorkspaceSchema.parse(JSON.parse(JSON.stringify(workspace)));
}

function collectEditableWorkspaceFilePaths(files: readonly SiteWorkspaceFile[]) {
  return files
    .filter((file) => file.path.startsWith('src/'))
    .map((file) => file.path);
}

function sanitizeTargetAlias(value: string) {
  const trimmed = value.trim();
  return trimmed.length >= 2 ? trimmed : null;
}

function dedupeTargetAliases(aliases: readonly string[]) {
  const seen = new Set<string>();

  return aliases.flatMap((alias) => {
    const sanitized = sanitizeTargetAlias(alias);
    if (!sanitized) return [];

    const key = sanitized.toLowerCase();
    if (seen.has(key)) return [];
    seen.add(key);
    return [sanitized];
  });
}

function getWorkspaceFileName(path: string) {
  return path.split('/').pop() ?? path;
}

function getWorkspaceFileStem(path: string) {
  return getWorkspaceFileName(path).replace(/\.(tsx|ts|json|md)$/u, '');
}

function buildRouteTargetAliases(page: SitePage) {
  return dedupeTargetAliases([
    page.title,
    page.path,
    buildRouteSlug(page),
    getWorkspaceFileStem(buildRouteFilePath(page)),
  ]);
}

function buildHelperTargetAliases(filePath: string) {
  const stem = getWorkspaceFileStem(filePath);

  if (filePath === 'src/lib/theme-presentation.ts') {
    return dedupeTargetAliases([
      'theme presentation',
      'theme',
      'presentation',
      'テーマ',
      'テーマ設定',
      stem,
      filePath,
    ]);
  }

  if (filePath === 'src/utils/cn.ts') {
    return dedupeTargetAliases([
      'cn',
      'class name',
      'class utility',
      stem,
      filePath,
    ]);
  }

  return dedupeTargetAliases([
    stem,
    stem.replace(/-/gu, ' '),
    filePath,
  ]);
}

function isAiHelperWorkspaceFilePath(path: string) {
  return path === 'src/lib/theme-presentation.ts'
    || path === 'src/utils/cn.ts'
    || path === 'src/components/sections/section-heading.tsx'
    || path === 'src/components/sections/section-image.tsx';
}

function buildWorkspaceTargets(
  document: SiteDocument,
  routes: readonly SiteWorkspaceRoute[],
  components: readonly SiteWorkspaceComponent[],
  files: readonly SiteWorkspaceFile[],
): SiteWorkspaceTarget[] {
  const filePathSet = new Set(files.map((file) => file.path));
  const targets: SiteWorkspaceTarget[] = [];

  for (const page of document.pages) {
    const route = routes.find((candidate) => candidate.pageId === page.id);
    if (!route || !filePathSet.has(route.entryFilePath)) continue;

    targets.push({
      path: route.entryFilePath,
      kind: 'route',
      label: page.title,
      aliases: buildRouteTargetAliases(page),
      pageId: page.id,
    });
  }

  for (const component of components) {
    if (filePathSet.has(component.filePath)) {
      targets.push({
        path: component.filePath,
        kind: 'section-instance',
        label: component.label,
        aliases: dedupeTargetAliases([component.label, component.sectionType, ...component.aliases]),
        pageId: component.pageId,
        sectionId: component.sectionId,
      });
    }

    const template = getSectionTemplateDefinition(component.sectionType as SiteSection['type']);
    if (!template || !filePathSet.has(template.filePath)) continue;

    targets.push({
      path: template.filePath,
      kind: 'section-template',
      label: component.label,
      aliases: dedupeTargetAliases([component.label, component.sectionType, ...component.aliases]),
      pageId: component.pageId,
      sectionId: component.sectionId,
    });
  }

  if (filePathSet.has('src/site-entry.tsx')) {
    targets.push({
      path: 'src/site-entry.tsx',
      kind: 'entry-file',
      label: 'workspace entry',
      aliases: [
        'site-entry',
        'entry',
        'workspace entry',
        'site entry',
        'src/site-entry.tsx',
        'app shell',
        'root component',
        'workspace root',
        'layout',
        'global layout',
        'header',
        'navigation',
        'nav',
        'ルートコンポーネント',
        'アプリ全体',
        '全体レイアウト',
        'ヘッダー',
        'ナビ',
        'ナビゲーション',
      ],
    });
  }

  for (const file of files) {
    if (!isAiHelperWorkspaceFilePath(file.path)) continue;
    targets.push({
      path: file.path,
      kind: 'helper',
      label: getWorkspaceFileStem(file.path),
      aliases: buildHelperTargetAliases(file.path),
    });
  }

  return targets.filter((target, index, currentTargets) => {
    const currentKey = [
      target.path,
      target.kind,
      target.pageId ?? '',
      target.sectionId ?? '',
      target.label,
    ].join('::');

    return currentTargets.findIndex((candidate) => [
      candidate.path,
      candidate.kind,
      candidate.pageId ?? '',
      candidate.sectionId ?? '',
      candidate.label,
    ].join('::') === currentKey) === index;
  });
}

function collectAiEditableWorkspaceFilePaths(targets: readonly SiteWorkspaceTarget[]) {
  return Array.from(new Set(targets.map((target) => target.path)));
}

function finalizeSiteWorkspace(workspace: SiteWorkspace): SiteWorkspace {
  const editableFilePaths = Array.from(new Set([
    ...collectEditableWorkspaceFilePaths(workspace.files),
    ...workspace.editableFilePaths,
  ]));
  const aiEditableFilePaths = Array.from(new Set([
    ...workspace.aiEditableFilePaths,
  ]));

  return siteWorkspaceSchema.parse({
    ...workspace,
    editableFilePaths,
    aiEditableFilePaths,
  });
}

function parseSiteWorkspaceRow(row: SiteWorkspaceRow): SiteWorkspace {
  return finalizeSiteWorkspace({
    ...siteWorkspaceSchema.parse(row.workspace),
    version: row.version,
    updatedAt: row.updated_at,
  });
}

function sanitizePackageName(siteName: string) {
  const normalized = siteName
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/^-+|-+$/gu, '');

  return normalized || 'chatlp-site-workspace';
}

function buildRouteSlug(page: SitePage) {
  const pathBased = page.path === '/'
    ? 'home'
    : page.path.replace(/^\/+/u, '').replace(/[^a-z0-9]+/giu, '-').replace(/^-+|-+$/gu, '');

  return pathBased || page.id.replace(/[^a-z0-9]+/giu, '-').replace(/^-+|-+$/gu, '') || 'page';
}

function buildRouteFilePath(page: SitePage) {
  return `src/routes/${buildRouteSlug(page)}.tsx`;
}

function sanitizeWorkspaceSegment(value: string) {
  const normalized = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/giu, '-')
    .replace(/^-+|-+$/gu, '');

  return normalized || 'section';
}

function buildSectionComponentFilePath(page: SitePage, section: SiteSection) {
  return `src/components/${buildRouteSlug(page)}/${sanitizeWorkspaceSegment(section.id)}.tsx`;
}

function getSectionLabel(section: SiteSection) {
  if ('title' in section && typeof section.title === 'string' && section.title.trim()) {
    return section.title.trim();
  }

  if ('headline' in section && typeof section.headline === 'string' && section.headline.trim()) {
    return section.headline.trim();
  }

  return section.type;
}

function buildWorkspaceComponentAliases(section: SiteSection) {
  const aliases = [
    getSectionLabel(section),
    section.type,
    ...collectSectionPromptCandidates(section).map((candidate) => candidate.text),
  ];
  const seen = new Set<string>();

  return aliases.filter((alias) => {
    const trimmed = alias.trim();
    if (trimmed.length < 2) return false;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function readWorkspaceDocumentExport(content: string) {
  const match = content.match(/export const siteDocument = ([\s\S]*?) as const;/u);
  if (!match?.[1]) {
    return null;
  }

  try {
    return siteDocumentSchema.parse(JSON.parse(match[1])) as SiteDocument;
  } catch {
    return null;
  }
}

function buildSiteDocumentFile(document: SiteDocument): SiteWorkspaceFile {
  return {
    path: 'src/site-document.ts',
    language: 'ts',
    purpose: 'Structured snapshot kept available while the workspace is still in document_bridge mode.',
    content: [
      'export const siteDocument = ',
      JSON.stringify(document, null, 2),
      ' as const;',
      '',
    ].join('\n'),
  };
}

function buildRouteFile(page: SitePage): SiteWorkspaceFile {
  return {
    path: buildRouteFilePath(page),
    language: 'tsx',
    purpose: 'Route-level bridge file. Replace this with custom JSX when this page becomes code-first.',
    content: [
      "import { siteDocument } from '../site-document';",
      '',
      formatBridgeRouteOverrideComment(),
      '',
      `export const pageId = ${JSON.stringify(page.id)};`,
      `export const pagePath = ${JSON.stringify(page.path)};`,
      `export const pageTitle = ${JSON.stringify(page.title)};`,
      '',
      '/**',
      ' * Bridge export for AI IDE mode.',
      ' * Replace structuredPage with custom React code when you migrate this route to code-first.',
      ' */',
      "export const structuredPage = siteDocument.pages.find((page) => page.id === pageId) ?? siteDocument.pages[0];",
      '',
    ].join('\n'),
  };
}

function buildSectionComponentFile(page: SitePage, section: SiteSection): SiteWorkspaceFile {
  return {
    path: buildSectionComponentFilePath(page, section),
    language: 'tsx',
    purpose: `${page.title} ページの ${getSectionLabel(section)} セクションに対応する component bridge file。表示変更はこの file の @chatlp-bridge JSON block に集約します。`,
    content: [
      "import { siteDocument } from '../../site-document';",
      '',
      formatBridgeRouteOverrideComment({
        sectionPresentationOverrides: [
          {
            sectionId: section.id,
          },
        ],
        sectionTextOverrides: [],
        itemTextOverrides: [],
      }),
      '',
      `export const pageId = ${JSON.stringify(page.id)};`,
      `export const sectionId = ${JSON.stringify(section.id)};`,
      `export const sectionType = ${JSON.stringify(section.type)};`,
      `export const sectionLabel = ${JSON.stringify(getSectionLabel(section))};`,
      '',
      '/**',
      ' * Section-level bridge export for AI IDE mode.',
      ' * Replace this file with custom JSX when this section becomes fully code-first.',
      ' */',
      'export const structuredSection = siteDocument.pages',
      '  .find((page) => page.id === pageId)',
      '  ?.sections.find((section) => section.id === sectionId) ?? null;',
      '',
    ].join('\n'),
  };
}

function buildSharedSectionTemplateFiles(document: SiteDocument): SiteWorkspaceFile[] {
  const templateFilePaths = new Set<string>();

  for (const page of document.pages) {
    for (const section of page.sections) {
      const template = getSectionTemplateDefinition(section.type);
      if (!template) continue;
      templateFilePaths.add(template.filePath);
    }
  }

  for (const entryFilePath of sharedWorkspaceCodeFirstEntryFilePaths) {
    templateFilePaths.add(entryFilePath);
  }

  return collectWorkspaceSourceDependencyFiles([...templateFilePaths]).map((file) => ({
    path: file.path,
    language: file.language,
    purpose: file.path.startsWith('src/components/sections/')
      ? 'Shared section renderer source snapshot for direct component editing in AI IDE mode.'
      : 'Shared section runtime helper snapshot used by AI IDE preview.',
    content: file.source,
  }));
}

function buildEntryFile(routes: readonly SiteWorkspaceRoute[]): SiteWorkspaceFile {
  const imports = routes
    .map((route, index) => `import * as route${index} from './routes/${route.entryFilePath.split('/').pop()?.replace(/\.tsx$/u, '')}';`)
    .join('\n');

  const routeList = routes
    .map((route, index) => [
      '  {',
      `    pageId: route${index}.pageId,`,
      `    path: route${index}.pagePath,`,
      `    title: route${index}.pageTitle,`,
      `    entryFilePath: ${JSON.stringify(route.entryFilePath)},`,
      '  },',
    ].join('\n'))
    .join('\n');

  const routeEntries = routes
    .map((route, index) => [
      '  {',
      `    route: workspaceRoutes[${index}],`,
      `    module: route${index},`,
      '  },',
    ].join('\n'))
    .join('\n');

  return {
    path: 'src/site-entry.tsx',
    language: 'tsx',
    purpose: 'Workspace manifest for the AI IDE runtime.',
    content: [
      '// @chatlp-managed-entry',
      "import { siteDocument } from './site-document';",
      "import { SiteSectionRenderer } from './components/sections';",
      imports,
      '',
      "export const workspaceFramework = 'react_vite' as const;",
      "export const workspaceMode = 'document_bridge' as const;",
      'export const workspaceDocument = siteDocument;',
      '',
      'export const workspaceRoutes = [',
      routeList,
      '] as const;',
      '',
      'export type StructuredWorkspaceRoute = typeof workspaceRoutes[number];',
      'export type StructuredWorkspacePage = typeof siteDocument.pages[number];',
      'export type StructuredWorkspaceTheme = typeof siteDocument.theme;',
      'export type WorkspaceEntryProps = {',
      '  route?: StructuredWorkspaceRoute | null;',
      '  page?: StructuredWorkspacePage | null;',
      '  theme?: StructuredWorkspaceTheme | null;',
      '  document?: typeof siteDocument | null;',
      '  currentPath?: string;',
      '  interactive?: boolean;',
      '};',
      '',
      'export const workspaceRouteEntries = [',
      routeEntries,
      '] as const;',
      '',
      'function normalizeWorkspaceCurrentPath(currentPath?: string) {',
      "  const normalized = `/${String(currentPath ?? '/').replace(/^\\/+/, '').replace(/\\/+$/u, '')}`;",
      "  return normalized === '/' || normalized === '' ? '/' : normalized;",
      '}',
      '',
      'export function getWorkspaceRouteEntry(currentPath?: string) {',
      '  const normalizedPath = normalizeWorkspaceCurrentPath(currentPath);',
      '  return workspaceRouteEntries.find((entry) => entry.route.path === normalizedPath)',
      "    ?? workspaceRouteEntries.find((entry) => entry.route.path === '/')",
      '    ?? workspaceRouteEntries[0]',
      '    ?? null;',
      '}',
      '',
      'export function getStructuredWorkspacePage(currentPath?: string) {',
      '  const entry = getWorkspaceRouteEntry(currentPath);',
      "  const structuredPage = entry?.module && 'structuredPage' in entry.module ? entry.module.structuredPage : null;",
      '  if (structuredPage) {',
      '    return structuredPage;',
      '  }',
      '',
      '  const normalizedPath = normalizeWorkspaceCurrentPath(currentPath);',
      '  return siteDocument.pages.find((page) => page.path === normalizedPath)',
      "    ?? siteDocument.pages.find((page) => page.path === '/')",
      '    ?? siteDocument.pages[0]',
      '    ?? null;',
      '}',
      '',
      'export function getStructuredWorkspaceTheme() {',
      '  return siteDocument.theme;',
      '}',
      '',
      'export function resolveStructuredWorkspaceContext(props: WorkspaceEntryProps = {}) {',
      '  const resolvedDocument = props.document ?? workspaceDocument;',
      '  const resolvedRoute = props.route ?? getWorkspaceRouteEntry(props.currentPath)?.route ?? null;',
      '  const resolvedPage = props.page ?? getStructuredWorkspacePage(props.currentPath);',
      '  const resolvedTheme = props.theme ?? getStructuredWorkspaceTheme();',
      '  return {',
      '    document: resolvedDocument,',
      '    route: resolvedRoute,',
      '    page: resolvedPage,',
      '    theme: resolvedTheme,',
      "    currentPath: normalizeWorkspaceCurrentPath(props.currentPath ?? resolvedRoute?.path ?? resolvedPage?.path ?? '/'),",
      '    interactive: props.interactive ?? false,',
      '  };',
      '}',
      '',
      'export function StructuredWorkspacePageRenderer(props: WorkspaceEntryProps) {',
      '  const resolved = resolveStructuredWorkspaceContext(props);',
      '  if (!resolved.page || !resolved.theme) {',
      '    return null;',
      '  }',
      '',
      "  const contentSections = resolved.page.sections.filter((section) => section.type !== 'footer');",
      "  const footerSection = resolved.page.sections.find((section) => section.type === 'footer') ?? null;",
      '',
      '  return (',
      '    <main className="min-h-screen bg-background text-primary" style={{ fontFamily: resolved.theme.fontFamily }}>',
      '      {contentSections.map((section) => (',
      '        <div key={section.id}>',
      '          <SiteSectionRenderer section={section} theme={resolved.theme} interactive={resolved.interactive} />',
      '        </div>',
      '      ))}',
      '      {footerSection ? (',
      '        <SiteSectionRenderer section={footerSection} theme={resolved.theme} interactive={resolved.interactive} />',
      '      ) : null}',
      '    </main>',
      '  );',
      '}',
      '',
      '/**',
      ' * The runtime can start from this manifest and later switch routes to custom React modules.',
      ' * Export default from this file when you want the whole workspace to become root-level code-first.',
      ' * Reuse WorkspaceEntryProps, getWorkspaceRouteEntry, getStructuredWorkspacePage, and getStructuredWorkspaceTheme',
      ' * when building a root WorkspaceApp component. StructuredWorkspacePageRenderer is the quickest',
      ' * way to keep the current page rendering while you layer custom app-shell UI around it.',
      ' */',
      'export const workspaceEntry = {',
      "  entryFilePath: 'src/site-entry.tsx',",
      "  mode: 'document_bridge' as const,",
      '};',
      '',
    ].join('\n'),
  };
}

function shouldRefreshManagedBridgeEntryFile(content: string) {
  if (hasWorkspaceRuntimeDefaultExport(content)) {
    return false;
  }

  if (content.includes('// @chatlp-managed-entry')) {
    return true;
  }

  return content.includes("export const workspaceFramework = 'react_vite' as const;")
    && content.includes('export const workspaceRoutes = [')
    && content.includes('export const workspaceEntry = {')
    && !content.includes('export const workspaceRouteEntries = [');
}

function shouldRefreshManagedBridgeRouteFile(content: string) {
  if (hasWorkspaceRuntimeDefaultExport(content)) {
    return false;
  }

  return content.includes("import { siteDocument } from '../site-document';")
    && content.includes('export const pageId = ')
    && content.includes('export const pagePath = ')
    && content.includes('export const pageTitle = ')
    && content.includes('export const structuredPage = ');
}

function shouldRefreshManagedBridgeSectionFile(content: string) {
  if (hasWorkspaceRuntimeDefaultExport(content)) {
    return false;
  }

  return content.includes("import { siteDocument } from '../../site-document';")
    && content.includes('export const sectionId = ')
    && content.includes('export const sectionType = ')
    && content.includes('export const sectionLabel = ')
    && content.includes('export const structuredSection = ');
}

function buildPackageJsonFile(document: SiteDocument): SiteWorkspaceFile {
  return {
    path: 'package.json',
    language: 'json',
    purpose: 'Starter package manifest for a per-site React/Vite workspace.',
    content: `${JSON.stringify({
      name: sanitizePackageName(document.siteName),
      private: true,
      version: '0.0.0',
      description: 'ChatLP AI IDE workspace starter',
      type: 'module',
    }, null, 2)}\n`,
  };
}

function buildReadmeFile(document: SiteDocument): SiteWorkspaceFile {
  return {
    path: 'README.md',
    language: 'md',
    purpose: 'Starter guidance for the generated site workspace.',
    content: [
      `# ${document.siteName}`,
      '',
      'This workspace is the AI IDE starter generated from the current SiteDocument snapshot.',
      '',
      '- `src/site-document.ts` keeps the latest structured snapshot for bridge mode.',
      '- `src/routes/*.tsx` is the first place to replace bridge exports with custom code.',
      '- `src/site-entry.tsx` acts as the runtime manifest while the workspace is still bootstrapping, and can become the root code-first entry by exporting a default component.',
      '',
      'When a route becomes fully code-first, keep the same `pagePath` but replace the bridge exports with real React code.',
      '',
    ].join('\n'),
  };
}

export function buildStarterSiteWorkspace(document: SiteDocument): SiteWorkspace {
  const routes: SiteWorkspaceRoute[] = document.pages.map((page) => ({
    pageId: page.id,
    title: page.title,
    path: page.path,
    entryFilePath: buildRouteFilePath(page),
  }));

  const components: SiteWorkspaceComponent[] = document.pages.flatMap((page) => page.sections.map((section) => ({
    pageId: page.id,
    sectionId: section.id,
    sectionType: section.type,
    label: getSectionLabel(section),
    filePath: buildSectionComponentFilePath(page, section),
    aliases: buildWorkspaceComponentAliases(section),
  })));

  const files: SiteWorkspaceFile[] = [
    buildPackageJsonFile(document),
    buildReadmeFile(document),
    buildSiteDocumentFile(document),
    ...buildSharedSectionTemplateFiles(document),
    ...document.pages.map((page) => buildRouteFile(page)),
    ...document.pages.flatMap((page) => page.sections.map((section) => buildSectionComponentFile(page, section))),
  ];

  files.push(buildEntryFile(routes));
  const targets = buildWorkspaceTargets(document, routes, components, files);

  return finalizeSiteWorkspace({
    version: Math.max(document.version, 1),
    framework: 'react_vite',
    mode: 'document_bridge',
    entryFilePath: 'src/site-entry.tsx',
    editableFilePaths: collectEditableWorkspaceFilePaths(files),
    aiEditableFilePaths: collectAiEditableWorkspaceFilePaths(targets),
    routes,
    components,
    targets,
    files,
    generatedFromDocumentVersion: document.version,
    updatedAt: document.updatedAt,
  });
}

export function syncDocumentBridgeWorkspace(
  workspace: SiteWorkspace,
  document: SiteDocument,
): SiteWorkspace {
  const starter = buildStarterSiteWorkspace(document);
  const existingFiles = new Map(workspace.files.map((file) => [file.path, file] as const));
  const starterFilePaths = new Set(starter.files.map((file) => file.path));

  const files = [
    ...starter.files.map((file) => (
      file.path === 'src/site-document.ts'
        ? file
        : file.path === 'src/site-entry.tsx'
          ? (
            existingFiles.get(file.path) && !shouldRefreshManagedBridgeEntryFile(existingFiles.get(file.path)?.content ?? '')
              ? existingFiles.get(file.path)!
              : file
          )
        : file.path.startsWith('src/routes/')
          ? (
            existingFiles.get(file.path) && !shouldRefreshManagedBridgeRouteFile(existingFiles.get(file.path)?.content ?? '')
              ? existingFiles.get(file.path)!
              : file
          )
        : file.path.startsWith('src/components/')
          && !file.path.startsWith('src/components/sections/')
          ? (
            existingFiles.get(file.path) && !shouldRefreshManagedBridgeSectionFile(existingFiles.get(file.path)?.content ?? '')
              ? existingFiles.get(file.path)!
              : file
          )
        : existingFiles.get(file.path) ?? file
    )),
    ...workspace.files.filter((file) => !starterFilePaths.has(file.path)),
  ];

  const editableFilePaths = Array.from(new Set([
    ...starter.editableFilePaths,
    ...workspace.editableFilePaths,
  ]));

  return finalizeSiteWorkspace({
    ...workspace,
    routes: starter.routes,
    components: starter.components,
    targets: starter.targets,
    entryFilePath: starter.entryFilePath,
    editableFilePaths,
    aiEditableFilePaths: Array.from(new Set([
      ...starter.aiEditableFilePaths,
      ...workspace.aiEditableFilePaths,
    ])),
    files,
    generatedFromDocumentVersion: document.version,
    updatedAt: document.updatedAt,
  });
}

export function buildWorkspacePersistenceSnapshot(
  workspace: SiteWorkspace,
  document: SiteDocument,
): SiteWorkspace {
  return syncDocumentBridgeWorkspace(workspace, document);
}

export function readWorkspaceDocumentSnapshot(workspace: SiteWorkspace) {
  const documentFile = workspace.files.find((file) => file.path === 'src/site-document.ts');
  if (!documentFile) {
    return null;
  }

  return readWorkspaceDocumentExport(documentFile.content);
}

export async function getSiteWorkspace(siteId: string): Promise<SiteWorkspace | null> {
  const row = await apiRequest<SiteWorkspaceRow | null>(
    `/api/sites/${encodeURIComponent(siteId)}/workspace`,
  );
  return row ? parseSiteWorkspaceRow(row) : null;
}

export async function persistSiteWorkspaceSnapshot(
  siteId: string,
  workspace: SiteWorkspace,
): Promise<SiteWorkspace> {
  const now = new Date().toISOString();
  const workspaceToPersist = finalizeSiteWorkspace({
    ...cloneWorkspace(workspace),
    updatedAt: now,
  });

  const persisted = await apiRequest<unknown>(
    `/api/sites/${encodeURIComponent(siteId)}/workspace`,
    {
      method: 'PUT',
      body: { workspace: workspaceToPersist },
    },
  );
  return finalizeSiteWorkspace(siteWorkspaceSchema.parse(persisted));
}

export async function listSiteWorkspaceRevisions(
  siteId: string,
  options?: { maxVersions?: number; maxDays?: number },
): Promise<SiteWorkspaceRevision[]> {
  const query = new URLSearchParams();
  if (options?.maxDays && options.maxDays > 0) {
    query.set('maxDays', String(options.maxDays));
  }
  if (options?.maxVersions && options.maxVersions > 0) {
    query.set('maxVersions', String(options.maxVersions));
  }
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  const rows = await apiRequest<unknown[]>(
    `/api/sites/${encodeURIComponent(siteId)}/workspace-revisions${suffix}`,
  );
  return rows.map((row) => siteWorkspaceRevisionSchema.parse(row));
}

export async function saveSiteWorkspaceRevision(
  siteId: string,
  workspace: SiteWorkspace,
  summary: string,
): Promise<{ workspace: SiteWorkspace; revision: SiteWorkspaceRevision }> {
  const validated = finalizeSiteWorkspace(cloneWorkspace(workspace));
  const result = await apiRequest<{ workspace: unknown; revision: unknown }>(
    `/api/sites/${encodeURIComponent(siteId)}/workspace-revisions`,
    {
      method: 'POST',
      body: { workspace: validated, summary },
    },
  );
  return {
    workspace: finalizeSiteWorkspace(siteWorkspaceSchema.parse(result.workspace)),
    revision: siteWorkspaceRevisionSchema.parse(result.revision),
  };
}

export async function ensureSiteWorkspaceFromDocument(
  siteId: string,
  document: SiteDocument,
  summary = 'AI IDE starter を作成',
): Promise<SiteWorkspace> {
  const existing = await getSiteWorkspace(siteId);
  if (existing) {
    if (
      existing.mode === 'document_bridge'
      && existing.generatedFromDocumentVersion !== document.version
    ) {
      const synced = syncDocumentBridgeWorkspace(existing, document);
      try {
        const result = await saveSiteWorkspaceRevision(
          siteId,
          synced,
          `Document v${document.version} と同期`,
        );
        return result.workspace;
      } catch (error) {
        if (isWorkspaceStorageUnavailableError(error)) {
          console.warn('site_workspace_storage_unavailable', {
            siteId,
            message: error instanceof Error ? error.message : 'Unknown workspace storage error.',
          });
          return synced;
        }
        throw error;
      }
    }

    return existing;
  }

  const starter = buildStarterSiteWorkspace(document);
  try {
    const result = await saveSiteWorkspaceRevision(siteId, starter, summary);
    return result.workspace;
  } catch (error) {
    if (isWorkspaceStorageUnavailableError(error)) {
      console.warn('site_workspace_storage_unavailable', {
        siteId,
        message: error instanceof Error ? error.message : 'Unknown workspace storage error.',
      });
      return starter;
    }
    throw error;
  }
}

export async function restoreSiteWorkspaceRevision(
  siteId: string,
  revisionId: string,
): Promise<{ workspace: SiteWorkspace; revision: SiteWorkspaceRevision }> {
  const row = await apiRequest<SiteWorkspaceRevisionRow>(
    `/api/sites/${encodeURIComponent(siteId)}/workspace-revisions/${encodeURIComponent(revisionId)}`,
  );
  const revisionWorkspace = finalizeSiteWorkspace(siteWorkspaceSchema.parse(row.workspace));
  return saveSiteWorkspaceRevision(siteId, revisionWorkspace, `復元: ${row.summary}`);
}
